export type PropertyStatus = 'Selling' | 'Sold Out' | 'Sale Closed'

export type PropertyCategory = 'house' | 'land'

export type Property = {
  id: string
  slug: string
  category: PropertyCategory
  name: string
  type?: string // For houses: Apartment, Bungalow, Duplex, etc.
  location: string
  city: string
  status: PropertyStatus
  // Pricing — Zylus does not publish prices publicly; these are indicative starting prices
  // based on Lagos/Abuja/Ogun market ranges for the area. Confirm with sales.
  priceFrom?: string
  plotSizes?: string[] // For land
  beds?: number
  baths?: number
  sqft?: string
  tagline: string
  intro: string
  features: string[]
  highlights: { label: string; value: string }[]
  gallery: string[]
  videoId?: string // YouTube video ID
  brochureUrl?: string // PDF link (placeholder until real one available)
  faqUrl?: string // Form & FAQ link
  flyerImage?: string // Promotional flyer
  priceTiers?: { label: string; price: string }[] // Optional breakdown of pricing variants
  priceGraphics?: string[] // Optional payment-plan graphics
  promoEndDate?: string // ISO date string for countdown timer
  hero: string
}

const placeholderPdf = '#' // Replace with real brochure URLs when available

// =========================
// HOUSES
// =========================
export const houses: Property[] = [
  {
    id: 'h2',
    slug: 'bolton-height',
    category: 'house',
    name: 'Bolton Height',
    type: 'Apartment',
    location: 'Lekki Phase 1, Lagos',
    city: 'Lagos',
    status: 'Selling',
    priceFrom: '₦270M',
    beds: 4,
    baths: 4,
    sqft: '2,800',
    tagline: 'Address of the city — Lekki Phase 1 living redefined.',
    intro:
      "Bolton Height is a flagship apartment block on the Lekki Phase 1 axis — the most coveted postcode in Lagos. Floor-to-ceiling glazing, double-height lobbies, and resort-grade amenities deliver the kind of city living usually reserved for the world's great capitals.",
    features: [
      'Electric Car Charging Stations',
      'Automated Home Features',
      'Swimming Pool',
      'World Class Recreational Area',
      'Gym, Lounge and more!',
      '24/7 Security & Power',
    ],
    highlights: [
      { label: 'Bedrooms', value: '4' },
      { label: 'Bathrooms', value: '4' },
      { label: 'Built-up', value: '2,800 sqft' },
      { label: 'Title', value: "Governor's Consent" },
    ],
    gallery: [
      'https://cdn.magicpatterns.com/uploads/emnrNKwaeXZJNCgZmEUfwr/DJI_0185.jpg',
      'https://cdn.magicpatterns.com/uploads/caFeBdzQ6APpYtxAEqWZPN/DJI_0186.jpg',
      'https://cdn.magicpatterns.com/uploads/smRiCYuDGoD98WoKCz5C5K/DJI_0202.jpg',
      'https://cdn.magicpatterns.com/uploads/kdXd928zYK6d9fpLKZmSHH/DJI_0209.jpg',
      'https://cdn.magicpatterns.com/uploads/5b9MUJrrBihXLxp8FhTAaz/DSC08049.jpg',
    ],
    videoId:
      'https://cdn.magicpatterns.com/uploads/hC6bia4KQQUAppTZM6Jkpp/WhatsApp Video 2026-05-12 at 10.01.54 AM.mp4',
    brochureUrl:
      'https://docs.google.com/presentation/d/1GTnYzmsnSMnFtVQr6-vvm8brylQp7baU/edit?usp=drive_link&ouid=111899183924647519554&rtpof=true&sd=true',
    faqUrl:
      'https://drive.google.com/file/d/1qudm7-BODB-GjabmYerButRvSAJb8WxD/view?usp=drive_link',
    flyerImage:
      'https://cdn.magicpatterns.com/uploads/qHi3vbWt1TWCNqLCRyhTPJ/WhatsApp_Image_2026-05-12_at_10.00.45_AM.jpg',
    hero: 'https://cdn.magicpatterns.com/uploads/emnrNKwaeXZJNCgZmEUfwr/DJI_0185.jpg',
  },
  {
    id: 'h3',
    slug: 'lekki-avana',
    category: 'house',
    name: 'Lekki Avana',
    type: 'Bungalow',
    location: 'Idera Scheme, Eleko, Epe, Lagos',
    city: 'Lagos',
    status: 'Sale Closed',
    priceFrom: 'From ₦42M',
    beds: 3,
    baths: 3,
    sqft: '1,600',
    tagline: 'Coastal calm meets contemporary craft.',
    intro:
      'Lekki Avana is a bungalow community tucked inside the Idera Scheme in Eleko — one of the fastest-appreciating corridors in Lagos. Designed for first-time owners and smart investors, the homes are built with timeless silhouettes and modern interiors.',
    features: [
      'Gated Estate',
      '24/7 Security',
      'Paved Roads & Drainage',
      'Streetlights',
      'Recreational Areas',
      'Boreholes & Treated Water',
      "Boys' Quarters Option",
      'Solar-ready Wiring',
    ],
    highlights: [
      { label: 'Bedrooms', value: '3' },
      { label: 'Bathrooms', value: '3' },
      { label: 'Built-up', value: '1,600 sqft' },
      { label: 'Title', value: 'Excision in Progress' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=85',
      'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?w=1200&q=85',
      'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=1200&q=85',
    ],
    videoId: 'cATYjVawmZ8',
    brochureUrl: placeholderPdf,
    hero: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1600&q=85',
  },
  {
    id: 'h4',
    slug: 'lekki-avana-phase-2',
    category: 'house',
    name: 'Lekki Avana Phase II',
    type: 'Bungalow',
    location: 'Eleran Igbe Bus Stop, Lagos',
    city: 'Lagos',
    status: 'Selling',
    priceFrom: 'From ₦78M',
    beds: 3,
    baths: 3,
    sqft: '1,700',
    tagline:
      'Phase Two of the most loved bungalow estate on the Lekki corridor.',
    intro:
      'Building on the success of Phase One, Lekki Avana Phase II expands the bungalow vision with larger plots, deeper landscaping, and a new generation of smart-ready homes. Available options include a 3-bedroom bungalow without pool for ₦78M, and a 4-bedroom bungalow without pool for ₦85M.',
    features: [
      'Gated Community',
      'CCTV Surveillance',
      'Underground Drainage',
      'Family Park',
      'Treated Water Supply',
      'Standby Power',
      'Premium Floor Tiles',
      'Designer Kitchens',
    ],
    highlights: [
      { label: 'Bedrooms', value: '3 - 4' },
      { label: 'Bathrooms', value: '3 - 4' },
      { label: 'Built-up', value: '1,700 sqft' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://cdn.magicpatterns.com/uploads/hF4Zfytvar8RExFt3Lur1k/DJI_0297.jpg',
      'https://cdn.magicpatterns.com/uploads/nYW2GVz4FnFCqJ5UaiEqjM/DJI_0299.jpg',
      'https://cdn.magicpatterns.com/uploads/37afyMHZi9kS8W5zg9zLX3/DJI_0277.jpg',
      'https://cdn.magicpatterns.com/uploads/q4Efnt5zmFgoT7P62KaKBJ/DJI_0288.jpg',
      'https://cdn.magicpatterns.com/uploads/ovDgDbRfGdpE8GEriT12hD/DJI_0298.jpg',
    ],
    priceTiers: [
      { label: '3 Bedroom Bungalow (without pool)', price: '₦78,000,000' },
      { label: '4 Bedroom Bungalow (without pool)', price: '₦85,000,000' },
    ],
    priceGraphics: [
      'https://cdn.magicpatterns.com/uploads/mfqJvZ9ARe4fzZBB1zm1FQ/WhatsApp_Image_2026-05-13_at_9.06.45_AM.jpg',
      'https://cdn.magicpatterns.com/uploads/1d2NMXfabYerN7T1s1sEVc/WhatsApp_Image_2026-05-13_at_9.06.40_AM.jpg',
      'https://cdn.magicpatterns.com/uploads/tFS3hf4cbf3F8NP3NfzHF4/WhatsApp_Image_2026-05-13_at_9.06.39_AM.jpg',
      'https://cdn.magicpatterns.com/uploads/i59rXs327x8sfrPnotQ4vu/WhatsApp_Image_2026-05-13_at_9.06.32_AM.jpg',
      'https://cdn.magicpatterns.com/uploads/ufbNi2hbzjq211KahXbZzX/WhatsApp_Image_2026-05-13_at_9.06.29_AM.jpg',
    ],
    videoId:
      'https://cdn.magicpatterns.com/uploads/wmtL21rE2X9GH3LwhApGXk/WhatsApp%20Video%202026-05-06%20at%201.15.16%20PM.mp4',
    brochureUrl:
      'https://drive.google.com/file/d/1t5lF1rJQvBNF0a0g6SVuX27uu2u63-eA/view?usp=drive_link',
    faqUrl:
      'https://drive.google.com/file/d/1t5lF1rJQvBNF0a0g6SVuX27uu2u63-eA/view?usp=drive_link',
    hero: 'https://cdn.magicpatterns.com/uploads/hF4Zfytvar8RExFt3Lur1k/DJI_0297.jpg',
  },
  {
    id: 'h5',
    slug: 'zylus-court-extension',
    category: 'house',
    name: 'Zylus Court Extension',
    type: 'Apartment',
    location: 'Bogije, Ibeju-Lekki, Lagos',
    city: 'Lagos',
    status: 'Selling',
    priceFrom: 'From ₦95M',
    beds: 4,
    baths: 4,
    sqft: '2,400',
    tagline:
      'The next chapter of the iconic Zylus Court — bigger, bolder, smarter.',
    intro:
      "Zylus Court Extension is the natural evolution of Zylus' signature Bogije estate. Set on the Lekki–Epe Expressway, the homes blend contemporary architecture with clever space planning, delivering luxury that is genuinely livable.",
    features: [
      '24/7 Manned Security',
      'CCTV & Access Control',
      'Smart Home Pre-wiring',
      'Backup Power Infrastructure',
      'Underground Drainage',
      'Streetlights & Paved Roads',
      'Swimming Pool & Clubhouse',
      "Children's Park",
    ],
    highlights: [
      { label: 'Bedrooms', value: '4' },
      { label: 'Bathrooms', value: '4' },
      { label: 'Built-up', value: '2,400 sqft' },
      { label: 'Title', value: 'C of O' },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&q=85',
      'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=1200&q=85',
      'https://images.unsplash.com/photo-1600585154363-67eb9e2e2099?w=1200&q=85',
      'https://images.unsplash.com/photo-1600573472592-401b489a3cdc?w=1200&q=85',
    ],
    videoId: 'kRJBlu-BIEQ',
    brochureUrl: placeholderPdf,
    flyerImage:
      'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&q=85',
    hero: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1600&q=85',
  },
  {
    id: 'l2',
    slug: 'zylus-chrystland-city',
    name: 'Zylus Chrystland City Bungalow and Resort',
    category: 'house',
    type: 'Bungalow',
    location: 'St Augustine University, Ilara-Epe, Epe, Lagos',
    city: 'Lagos',
    status: 'Selling',
    priceFrom: '₦58M',
    beds: 3,
    baths: 3,
    sqft: '1,800',
    tagline: 'Your infinite leisure escape.',
    intro:
      'Zylus Chrystland City Bungalow and Resort is positioned right next to St Augustine University in Ilara-Epe. Offering semi-finished 3 Bedroom Bungalows with BQ, it is an ideal investment for both end-users and smart investors looking for a resort-style living experience.',
    features: [
      'Estate Perimeter Fence',
      '24hrs Security',
      'Good Interlocking',
      'Swimming Pool & Cabana',
      'Solar Power Supply',
      'Good Drainage System',
      'Tennis Court',
      'Camera Surveillance',
      'Recreational Park',
    ],
    highlights: [
      { label: 'Bedrooms', value: '3 + BQ' },
      { label: 'Bathrooms', value: '3' },
      { label: 'Title', value: 'Government Allocation' },
      { label: 'Payment', value: 'Up to 12 months' },
    ],
    gallery: [
      'https://cdn.magicpatterns.com/uploads/iz63wiKXYjdCPKth2uQXnD/DJI_0921.jpg',
      'https://cdn.magicpatterns.com/uploads/fXyvzg9LYSAaq69jP4bzSD/DJI_0945.jpg',
      'https://cdn.magicpatterns.com/uploads/mJNDKERZwXSEid1UVPXAD3/DJI_0918.jpg',
      'https://cdn.magicpatterns.com/uploads/sbC6T4PwJEinGGWV5JkRwE/DJI_0928.jpg',
      'https://cdn.magicpatterns.com/uploads/jHuoct2LMarrroxPX2Qhqk/DJI_0943.jpg',
    ],
    priceTiers: [
      {
        label: '3 Bedroom Bungalow + BQ (Semi-Finished)',
        price: '₦58,000,000',
      },
    ],
    priceGraphics: [
      'https://cdn.magicpatterns.com/uploads/7sPRE4KXsxCHEVANo9XgQL/WhatsApp_Image_2026-05-04_at_9.08.25_AM.jpg',
      'https://cdn.magicpatterns.com/uploads/34JtSAU6PxfGUnMaX2ttHw/WhatsApp_Image_2026-05-04_at_9.08.28_AM.jpg',
      'https://cdn.magicpatterns.com/uploads/mJjKyeJanePcaJ3PZSviG8/WhatsApp_Image_2026-05-04_at_9.08.29_AM.jpg',
    ],
    promoEndDate: '2026-06-30T23:59:59Z',
    videoId:
      'https://cdn.magicpatterns.com/uploads/4Jw4qLJtFp5xcpxXFGf4zE/chrystland.mp4',
    brochureUrl:
      'https://drive.google.com/file/d/1X1b3TQyEMPbzavjHWAQnOEkGKf7tmMY-/view?usp=drive_link',
    faqUrl:
      'https://drive.google.com/file/d/1X1b3TQyEMPbzavjHWAQnOEkGKf7tmMY-/view?usp=drive_link',
    hero: 'https://cdn.magicpatterns.com/uploads/iz63wiKXYjdCPKth2uQXnD/DJI_0921.jpg',
  },
]

// =========================
// LAND
// =========================
export const lands: Property[] = []

export const allProperties: Property[] = [...houses, ...lands]

export function getPropertyBySlug(slug: string): Property | undefined {
  return allProperties.find((p) => p.slug === slug)
}

export function getRelated(p: Property, count = 3): Property[] {
  const pool = (p.category === 'house' ? houses : lands).filter(
    (x) => x.slug !== p.slug,
  )
  // Prefer same status & city
  return [...pool]
    .sort((a, b) => {
      const aScore =
        (a.status === p.status ? 2 : 0) + (a.city === p.city ? 1 : 0)
      const bScore =
        (b.status === p.status ? 2 : 0) + (b.city === p.city ? 1 : 0)
      return bScore - aScore
    })
    .slice(0, count)
}
